import type { NewsEvent } from './types';

export interface Section<T> { available: boolean; rows: T[] | null; error: string | null }
export interface IntelligenceNews extends NewsEvent {
  analysis_state: 'current' | 'pending' | 'failed' | 'stale' | 'unverified';
  matched_count?: number;
}
export interface NewsSummary {
  total: number; current: number; pending: number; failed: number; stale: number;
  unverified: number; high_threat: number; latest_published: string | null;
  latest_collected: string | null; latest_scored: string | null; avg_scoring_seconds: number | null;
}
export interface DomainSummary {
  domain: string; articles: number; current: number; avg_impact: number | null;
  avg_confidence: number | null; positive: number; negative: number; neutral: number;
  latest_published: string | null;
}
export interface TaskSummary {
  task: string; model: string; provider: string; calls: number; success: number;
  fallback: number; failed: number; tokens: number; cost_usd: number;
  avg_latency_ms: number; latest_at: string;
}
export interface CallFailure { call_id: string; ts: string; task: string; model: string; status: string; error_message: string | null }
export interface OutputQuality { state: string; issues: string[]; warnings: string[]; basis: string; grounding_verdict?: boolean | null }
export interface SavedReport { grounding_review?: { call_id: string; accepted: boolean; state?: string }; output_quality?: OutputQuality; call_id: string; ts: string; model: string; status: string; response_content: string | null }
export interface AlphaReview {
  review_date: string; review_ts: string; signal_samples: number; regime_samples: number;
  signal_accuracy_pct: number | null; regime_validity_pct: number | null;
  analysis_text: string | null; llm_call_id: string | null; alpha_decay_alert: boolean;
  trace_available?: boolean; metadata?: { strategy?: string };
}
export interface IncidentReport {
  incident_id: string; incident_type: string; detected_at: string; summary: string;
  severity: string; rca_status: string; rca_report: string | null; rca_call_id: string | null;
  rca_completed_at: string | null; trace_available?: boolean;
}
export interface DataMetric {
  metric: string; unit: string | null; observations: number; instruments: number;
  latest_at: string; flagged: number;
}
export interface IntelligenceOverview {
  as_of: string; hours: number; source: string;
  news: Section<NewsSummary>; domains: Section<DomainSummary>;
  analyses: Section<IntelligenceNews>; tasks: Section<TaskSummary>;
  failures: Section<CallFailure>; daily_reports: Section<SavedReport>;
  alpha_reviews: Section<AlphaReview>; incidents: Section<IncidentReport>;
  data_catalog: Section<DataMetric>;
}
export const DOMAIN_NAMES: Record<string, string> = {
  KR_SEMI_DIRECT: '한국 반도체', GLOBAL_SEMI_TECH: '글로벌 반도체 기술',
  TRUMP_TRADE_POLICY: '통상·관세', BIGTECH_AI_CAPEX: '빅테크·AI 설비투자',
  GEOPOLITICS_WAR_ENERGY: '지정학·전쟁·에너지', MACRO_BONDS_FX: '금리·채권·환율',
  SEMI_SUPPLY_CHAIN: '반도체 공급망', SEMI_COMPETITION: '반도체 경쟁',
  SEMI_DEMAND: '반도체 수요', AI_CAPEX: 'AI 설비투자', TRADE_TARIFF: '통상·관세',
};
export const domainName = (name: string) => DOMAIN_NAMES[name] ?? name;
export const ANALYSIS_NAMES: Record<IntelligenceNews['analysis_state'], string> = {
  current: '현행 입력 분석', pending: '분석 대기', failed: '분석 실패·대체값',
  stale: '이전 입력 분석', unverified: '분석 근거 미확인',
};
